/**
 * utils/api.js - Módulo para manejar solicitudes a la API
 */

export const buildApiUrl = (endpoint) => {
  if (typeof endpoint !== 'string') {
    console.warn("⚠️ Warning: 'endpoint' debe ser un string válido.");
    return "";
  }

  const baseUrl = autocrm_globals.api_url.replace(/\/$/, ''); // Remueve '/' final si existe
  const cleanEndpoint = endpoint.replace(/^\//, ''); // Remueve '/' inicial si existe
  return `${baseUrl}/${cleanEndpoint}`;
};

/**
 * Función genérica para manejar peticiones `fetch` con mejor control de errores y cancelación de solicitudes.
 * @param {string} endpoint - Ruta del API (ej. '/databases').
 * @param {Object} options - Opciones de `fetch` (ej. { method: 'POST', body: {...} }).
 * @returns {Promise<Object>} - Respuesta JSON o error manejado.
 */
export async function fetchApi(endpoint, options = {}) {
  if (!endpoint) {
    throw new Error('❌ [fetchApi] No se proporcionó un endpoint válido.');
  }

  const controller = new AbortController();
  const { signal } = controller;

  const headers = {
    'X-WP-Nonce': autocrm_globals.nonce,
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  try {
    const response = await fetch(buildApiUrl(endpoint), { ...options, headers, signal });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || `Error ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      console.warn('⚠️ [fetchApi] Solicitud cancelada:', endpoint);
    } else {
      console.error('❌ [fetchApi] Error en la solicitud:', error);
    }
    throw error;
  }
}

/**
 * Obtiene datos ejecutando un procedimiento almacenado específico.
 * @param {Object} params - Parámetros para la solicitud.
 * @returns {Promise<Object>} - Datos obtenidos del procedimiento.
 */
export async function fetchGroupData({ database, procedureName, fechaInicio, fechaFin, perPage }) {
  if (!database || !procedureName || !fechaInicio || !fechaFin || !perPage) {
    throw new Error('❌ [fetchGroupData] Parámetros incompletos.');
  }

  return fetchApi('/execute_procedure', {
    method: 'POST',
    body: JSON.stringify({
      database,
      procedure: procedureName,
      params: {
        fechaInicio,
        fechaFin,
        perPage: parseInt(perPage, 10),
        page: 1,             // Agregado para cumplir con el SP
        p_column_filter: '', // Agregado para evitar errores
        p_field_filter: '',  // Agregado para evitar errores
      },
    }),
  });
}

/**
 * Obtiene datos de un grupo específico usando `unique_key`.
 * @param {Object} params - Parámetros para la solicitud.
 * @returns {Promise<Object>} - Datos filtrados del grupo.
 */
export async function fetchGroupDataByKey({ unique_key, fechaInicio, fechaFin, perPage }) {
  if (!unique_key || !fechaInicio || !fechaFin || !perPage) {
    throw new Error('❌ [fetchGroupDataByKey] Parámetros incompletos.');
  }

  return fetchApi('/get_unified_group_data', {
    method: 'POST',
    body: JSON.stringify({
      unique_key,
      parameters: {
        fechaInicio,
        fechaFin,
        perPage: parseInt(perPage, 10),
        page: 1,             // Agregado para cumplir con el SP
        p_column_filter: '', // Agregado para evitar errores
        p_field_filter: '',  // Agregado para evitar errores
      },
    }),
  });
}

